import type { I18n } from "@lingui/core";
import { useLingui } from "@lingui/react";
import { useModals } from "@mobility-item-page/dealer/client";
import type { MobilityAd } from "@mobility-item-page/types";
import { isPrivateAdvertiser } from "@mobility-item-page/utilities";
import { Button } from "@warp-ds/elements/react/button";
import { useAdViewAd, useAdViewMeta } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";

/** The EV info expandable a contact action belongs to */
export enum ContactTopic {
  BatteryHealth,
  BatteryWarranty,
}

type TopicContent = {
  label: string;
  /** Sets `object.name` on the pulse event, so clicks can be told apart from the main contact button */
  trackingObjectName: string;
};

/**
 * Everything that differs per topic. The prefilled message goes here too, once the messaging
 * podlet supports one — see the note in the component below.
 */
const getTopicContent = (topic: ContactTopic, i18n: I18n, adViewType: MobilityAd["adViewType"]): TopicContent => {
  switch (topic) {
    case ContactTopic.BatteryHealth:
      return {
        label: i18n.t({
          id: "itemPageLayout.ev.rangeAndBattery.batteryHealth.action",
          message: "Ask the seller for a battery health test",
          comment: "Link that opens the message form, in the battery health expandable on electric car ads",
        }),
        trackingObjectName: "batteryHealth",
      };
    case ContactTopic.BatteryWarranty:
      return {
        label:
          adViewType === "car-new"
            ? i18n.t({
                id: "itemPageLayout.ev.rangeAndBattery.batteryWarranty.action.carNew",
                message: "Ask the seller about battery warranty",
                comment: "Link that opens the message form, in the battery warranty expandable on new electric car ads",
              })
            : i18n.t({
                id: "itemPageLayout.ev.rangeAndBattery.batteryWarranty.action",
                message: "Ask the seller about remaining coverage",
                comment: "Link that opens the message form, in the battery warranty expandable on electric car ads",
              }),
        trackingObjectName: "batteryWarranty",
      };
  }
};

type Props = {
  topic: ContactTopic;
};

/**
 * "Ask the seller" link used inside the EV info expandables. Dealer ads open the dealer contact
 * flow, private ads use the messaging podlet — the same one behind the main "Send message" button.
 */
export const ContactAction = ({ topic }: Props) => {
  const { i18n } = useLingui();
  const { isLoggedIn, isPreview } = useAppConfig();
  const { openContactFlow } = useModals();
  const { ownerUrn } = useAdViewMeta();
  const { adViewType } = useAdViewAd();

  const { label, trackingObjectName } = getTopicContent(topic, i18n, adViewType);

  if (isPreview) {
    return (
      <Button variant="link" disabled>
        {label}
      </Button>
    );
  }

  if (isPrivateAdvertiser(ownerUrn)) {
    // NOTE: finn-contact-button has no prefill prop yet (v1.0.130) — its destination URL comes from
    // the podlet's own #contact-button-data blob. Add a per-topic message here once it does.
    // <w-button full-width> is hardcoded inside the podlet, so keep the link from filling the card
    return (
      <div className="w-fit">
        {/* @ts-expect-error TODO: fix the custom element typing */}
        <finn-contact-button buttonVariant="link" buttonText={label} trackingObjectName={trackingObjectName} />
      </div>
    );
  }

  const openContactForm = () => {
    /* trackContactUs(position); */
    if (!isLoggedIn) {
      const { origin, pathname } = window.location;
      const loginUrl = new URL(`${origin}/auth/login`);
      loginUrl.searchParams.set("redirectUrl", `${origin}${pathname}?contact`);
      loginUrl.searchParams.set("cancelUrl", `${origin}${pathname}`);
      window.location.href = loginUrl.toString();
      return;
    }
    openContactFlow();
  };

  return (
    <Button variant="link" onClick={openContactForm}>
      {label}
    </Button>
  );
};
